/* ============================================================
 * GSS Postcode Gating + Regional Routing
 * Built on the original gating script by Grassick Group Media.
 *
 * Reads the zone saved by gss-finder.js (localStorage key gss_v1_zone)
 * and hides product cards/groups not available in that depot's range.
 * Browsing-only: does not redirect, does not block direct product URLs,
 * does not affect cart or checkout.
 *
 * NOTE: DEPOT_HANDLES below is a MANUAL list. It is NOT driven by
 * Shopify inventory levels or collection membership.
 *
 * ------------------------------------------------------------
 * REGIONAL TIER - added August 2026
 * ------------------------------------------------------------
 * gss-finder.js only knows metro postcodes. Any other valid 4-digit
 * postcode falls through to its 'not in our system' message. This file
 * extends the finder WITHOUT modifying it, so future updates to
 * gss-finder.js do not clash.
 *
 * COVERAGE: NSW, ACT and Victoria only. Regional QLD/SA/WA/TAS/NT
 * still fall through to the finder's existing contact-sales message.
 *
 * ------------------------------------------------------------
 * METRO GAP FIX - added August 2026
 * ------------------------------------------------------------
 * The finder's metro lists are incomplete. It covers only 121 of the
 * 250 postcode slots in 2000-2249 and none of Macarthur or the
 * Penrith/Blacktown corridor; Melbourne covers 191 of 211 in 3000-3210.
 * Left alone, the regional tier would swallow Hurstville, Manly,
 * Cronulla, Marrickville, Penrith, Campbelltown, Greensborough,
 * Ringwood and roughly 190 other metro codes and price them +$50.
 * METRO_FALLBACK_RANGES catches those before regional resolution runs.
 * The proper fix is to complete the lists inside gss-finder.js.
 * ============================================================ */
(function () {
  'use strict';

  var KEY = 'gss_v1_zone';
  var SUFFIX = '-regional';
  var CACHE_KEY = 'gss_v1_regional_handles';
  var PIN = String.fromCodePoint(128205);

  var DEPOT_HANDLES = {
    wyee: null,
    brisbane: ['light-duty-steel-shelving', 'medium-duty-steel-shelving-2m'],
    melbourne: ['light-duty-steel-shelving', 'medium-duty-steel-shelving-2m'],
    adelaide: ['light-duty-steel-shelving-2m-1-85m-high-in-one-box', 'medium-duty-steel-shelving-2m-in-one-box', 'medium-duty-steel-shelving-2m'],
    perth: ['light-duty-steel-shelving-2m-1-85m-high-in-one-box', 'medium-duty-steel-shelving-2m-in-one-box', 'medium-duty-steel-shelving-1-5m', 'medium-duty-steel-shelving-2m']
  };

  var REGIONS = {
    act: { label: 'the Canberra region', url: '/collections/garage-shelving-canberra' },
    nsw: { label: 'regional NSW', url: '/collections/garage-shelving-nsw-regional' },
    vic: { label: 'regional Victoria', url: '/collections/garage-shelving-victoria-regional' }
  };

  var REGIONAL_FALLBACK = [
    'medium-duty-steel-shelving-1m-regional',
    'medium-duty-steel-shelving-1-5m-regional',
    'medium-duty-steel-shelving-2m-regional',
    'medium-duty-steel-shelving-2-4m-regional',
    'heavy-duty-steel-shelving-regional',
    'light-duty-steel-shelving-regional',
    'heavy-duty-steel-bench-regional',
    'medium-duty-workbench-regional',
    'castor-wheels-regional'
  ];

  /* Metro postcode ranges the finder's own lists miss. Checked BEFORE
     regional resolution so metro customers are never priced as regional. */
  var METRO_FALLBACK_RANGES = [
    [2000, 2249, 'Sydney'],
    [2555, 2574, 'Sydney'],
    [2745, 2770, 'Sydney'],
    [3000, 3210, 'Melbourne']
  ];

  var REMOVED = [];

  /* ---------- small helpers ---------- */

  function endsWith(str, suffix) {
    return str.length >= suffix.length && str.slice(str.length - suffix.length) === suffix;
  }

  function load() {
    try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
  }

  function save(z) {
    try {
      localStorage.setItem(KEY, JSON.stringify(z));
      window.dispatchEvent(new Event('gss:zone-updated'));
    } catch (e) {}
  }

  function depotOf(z) {
    if (!z) { return null; }
    if (typeof z === 'string') { return z; }
    return z.depot || null;
  }

  /* ---------- regional range ---------- */

  function regionalHandles() {
    if (window.GSS_REGIONAL_HANDLES && window.GSS_REGIONAL_HANDLES.length) {
      return window.GSS_REGIONAL_HANDLES;
    }
    var cached = null;
    try { cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null'); } catch (e) {}
    window.GSS_REGIONAL_HANDLES = (cached && cached.length) ? cached : REGIONAL_FALLBACK.slice();
    return window.GSS_REGIONAL_HANDLES;
  }

  function refreshRegionalHandles() {
    if (!window.fetch) { return; }
    fetch('/collections/regional-australia/products.json?limit=50', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.products || !data.products.length) { return; }
        var handles = [];
        for (var i = 0; i < data.products.length; i++) { handles.push(data.products[i].handle); }
        window.GSS_REGIONAL_HANDLES = handles;
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(handles)); } catch (e) {}
        applyGating();
        renderTwinBanner();
      })
      .catch(function () {});
  }

  /* ---------- postcode resolution ---------- */

  function inMetroFallback(n) {
    for (var i = 0; i < METRO_FALLBACK_RANGES.length; i++) {
      if (n >= METRO_FALLBACK_RANGES[i][0] && n <= METRO_FALLBACK_RANGES[i][1]) {
        return METRO_FALLBACK_RANGES[i][2];
      }
    }
    return null;
  }

  function findRegion(pc) {
    var raw = String(pc || '').trim();
    if (raw.length !== 4) { return null; }
    var n = parseInt(raw, 10);
    if (!n) { return null; }
    if (n >= 2600 && n <= 2622) { return 'act'; }
    if (n >= 2900 && n <= 2920) { return 'act'; }
    if (n >= 2580 && n <= 2583) { return 'act'; }
    if (n >= 2000 && n <= 2999) { return 'nsw'; }
    if (n >= 3000 && n <= 3999) { return 'vic'; }
    return null;
  }

  /* ---------- finder result override ---------- */

  function handleFinderResult() {
    var res = document.getElementById('gssf-result');
    var input = document.getElementById('gssf-input');
    if (!res || !input) { return; }
    if (res.className.indexOf('gssf-out') === -1) { return; }

    var raw = String(input.value || '').trim();
    var n = parseInt(raw, 10);

    /* Metro gap check must run BEFORE regional resolution, or a large
       part of Sydney and parts of Melbourne get priced as regional. */
    var metroCity = (raw.length === 4 && n) ? inMetroFallback(n) : null;
    if (metroCity) {
      var isMelbourne = metroCity === 'Melbourne';
      res.className = 'gssf-result gssf-ok';
      res.innerHTML =
        '<strong>Good news, we ' + (isMelbourne ? 'offer metro pick up or delivery in ' : 'deliver to ') + metroCity + '.</strong>' +
        '<p>You can order online with pickup or delivery at checkout.</p>' +
        '<a class="gssf-cta" href="/collections/shop-all">Shop available range</a>';
      save({
        city: metroCity,
        depot: isMelbourne ? 'melbourne' : 'wyee',
        fullRange: !isMelbourne
      });
      renderBar();
      applyGating();
      return;
    }

    var region = findRegion(raw);
    if (!region) { return; }

    var r = REGIONS[region];
    res.className = 'gssf-result gssf-ok';
    res.innerHTML =
      '<strong>Good news, we deliver to ' + r.label + '.</strong>' +
      '<p>Regional pricing includes the area delivery charge, so the price on the page is the delivered price. Pickup from our Albury and Yass depots is free.</p>' +
      '<a class="gssf-cta" href="' + r.url + '">Shop the regional range</a>';

    save({
      city: r.label,
      depot: 'regional',
      tier: 'regional',
      region: region,
      collection: r.url,
      fullRange: false
    });
    renderBar();
  }

  function hookFinder() {
    document.addEventListener('click', function (e) {
      if (!e.target || e.target.id !== 'gssf-go') { return; }
      setTimeout(handleFinderResult, 0);
    });
    document.addEventListener('keydown', function (e) {
      if (!e.target || e.target.id !== 'gssf-input') { return; }
      if (e.key !== 'Enter') { return; }
      setTimeout(handleFinderResult, 0);
    });
  }

  /* ---------- location bar ---------- */

  function renderBar() {
    var z = load();
    if (!z) { return; }
    var bar = document.getElementById('gssf-bar');
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'gssf-bar';
      bar.innerHTML =
        '<span class="gssf-bar-pin">' + PIN + '</span>' +
        '<span class="gssf-bar-lead"></span> ' +
        '<span class="gssf-bar-city"></span> ' +
        '<button class="gssf-bar-change" type="button">Change</button>';
      document.body.insertBefore(bar, document.body.firstChild);
      bar.querySelector('.gssf-bar-change').addEventListener('click', function () {
        var ov = document.getElementById('gssf-overlay');
        if (ov) { ov.classList.add('gssf-open'); }
      });
    }
    var lead = bar.querySelector('.gssf-bar-lead');
    var city = bar.querySelector('.gssf-bar-city');
    if (lead) {
      lead.textContent = (z.tier === 'regional' || z.fullRange) ? 'Delivering to' : 'Metro pick up or delivery in';
    }
    if (city) { city.textContent = z.city || ''; }
  }

  /* ---------- wrong-tier banner on product pages ---------- */

  function productHandle() {
    var path = (window.location.pathname || '').toLowerCase();
    var i = path.indexOf('/products/');
    if (i === -1) { return null; }
    var rest = path.slice(i + 10);
    var cut = rest.indexOf('/');
    if (cut !== -1) { rest = rest.slice(0, cut); }
    cut = rest.indexOf('?');
    if (cut !== -1) { rest = rest.slice(0, cut); }
    return rest || null;
  }

  function injectTwinStyles() {
    if (document.getElementById('gssf-twin-style')) { return; }
    var style = document.createElement('style');
    style.id = 'gssf-twin-style';
    style.textContent =
      '#gssf-twin{display:flex;align-items:center;gap:9px;flex-wrap:wrap;' +
      'background:#fdf2ec;border:1px solid #f6d8c6;border-radius:12px;' +
      'padding:12px 15px;margin:0 0 18px;font-family:Inter,system-ui,-apple-system,sans-serif;' +
      'font-size:14px;line-height:1.45;color:#1a2a4b;}' +
      '#gssf-twin .gssf-twin-pin{color:#fe6b04;}' +
      '#gssf-twin .gssf-twin-cta{color:#fe6b04;font-weight:700;text-decoration:underline;}';
    document.head.appendChild(style);
  }

  function renderTwinBanner() {
    var old = document.getElementById('gssf-twin');
    if (old && old.parentNode) { old.parentNode.removeChild(old); }

    var z = load();
    var handle = productHandle();
    if (!z || !handle) { return; }

    var handles = regionalHandles();
    var onRegionalPage = endsWith(handle, SUFFIX);
    var isRegionalZone = z.tier === 'regional';
    var target, msg, cta;

    if (isRegionalZone && !onRegionalPage) {
      if (handles.indexOf(handle + SUFFIX) === -1) { return; }
      target = '/products/' + handle + SUFFIX;
      msg = 'You are shopping from ' + (z.city || 'a regional area') + '. This is the metro price.';
      cta = 'View the regional-priced version';
    } else if (!isRegionalZone && onRegionalPage) {
      target = '/products/' + handle.slice(0, handle.length - SUFFIX.length);
      msg = 'You are shopping from ' + (z.city || 'a metro area') + '. This listing is priced for regional delivery.';
      cta = 'View the standard version';
    } else {
      return;
    }

    injectTwinStyles();

    var banner = document.createElement('div');
    banner.id = 'gssf-twin';
    banner.innerHTML =
      '<span class="gssf-twin-pin">' + PIN + '</span>' +
      '<span class="gssf-twin-msg"></span>' +
      '<a class="gssf-twin-cta"></a>';
    banner.querySelector('.gssf-twin-msg').textContent = msg;
    var link = banner.querySelector('.gssf-twin-cta');
    link.textContent = cta;
    link.setAttribute('href', target);

    var anchor = document.querySelector('.product-information, product-form-component, .product-form');
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(banner, anchor);
      return;
    }
    var main = document.getElementById('MainContent');
    if (main) { main.insertBefore(banner, main.firstChild); }
  }

  /* ---------- card gating ---------- */

  function restoreAll() {
    for (var i = REMOVED.length - 1; i >= 0; i--) {
      var rec = REMOVED[i];
      if (!rec.element.isConnected && rec.parent) {
        if (rec.nextSibling && rec.nextSibling.isConnected) {
          rec.parent.insertBefore(rec.element, rec.nextSibling);
        } else {
          rec.parent.appendChild(rec.element);
        }
      }
      rec.element.removeAttribute('data-gss-hidden');
    }
    REMOVED = [];
  }

  function removeCard(card) {
    if (card.hasAttribute('data-gss-hidden')) { return; }
    card.setAttribute('data-gss-hidden', '1');
    REMOVED.push({ element: card, parent: card.parentNode, nextSibling: card.nextSibling });
    card.parentNode.removeChild(card);
  }

  function updateGroups() {
    document.querySelectorAll('[data-gss-group]').forEach(function (group) {
      var remaining = group.querySelectorAll('[data-gss-handle]');
      group.style.display = remaining.length > 0 ? '' : 'none';
    });
  }

  function showEverything() {
    restoreAll();
    document.querySelectorAll('[data-gss-group]').forEach(function (el) { el.style.display = ''; });
  }

  function applyGating() {
    var depot = depotOf(load());

    restoreAll();

    if (depot === 'regional') {
      var twins = regionalHandles();
      document.querySelectorAll('[data-gss-handle]').forEach(function (card) {
        var handle = card.getAttribute('data-gss-handle');
        if (!handle) { return; }
        if (endsWith(handle, SUFFIX)) { return; }
        if (twins.indexOf(handle + SUFFIX) !== -1) { removeCard(card); }
      });
      updateGroups();
      return;
    }

    var fullRange = !depot ||
      depot === 'wyee' ||
      !DEPOT_HANDLES.hasOwnProperty(depot) ||
      DEPOT_HANDLES[depot] === null;

    if (fullRange) {
      showEverything();
      return;
    }

    var allowed = DEPOT_HANDLES[depot];

    document.querySelectorAll('[data-gss-handle]').forEach(function (card) {
      var handle = card.getAttribute('data-gss-handle');
      if (allowed.indexOf(handle) === -1) { removeCard(card); }
    });

    updateGroups();
  }

  /* ---------- wiring ---------- */

  function onReady() {
    hookFinder();
    applyGating();
    renderBar();
    renderTwinBanner();
    refreshRegionalHandles();
  }

  function onZoneUpdated() {
    applyGating();
    renderBar();
    renderTwinBanner();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  window.addEventListener('gss:zone-updated', onZoneUpdated);
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) { onZoneUpdated(); }
  });

  if (window.MutationObserver) {
    var debounceTimer = null;
    var observer = new MutationObserver(function (mutations) {
      var hasAdditions = mutations.some(function (m) { return m.addedNodes && m.addedNodes.length > 0; });
      if (!hasAdditions) { return; }
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(applyGating, 50);
    });
    document.addEventListener('DOMContentLoaded', function () {
      var grid = document.querySelector('.product-grid, .gss-shopall');
      if (grid) { observer.observe(grid, { childList: true, subtree: true }); }
    });
  }
})();
